
import React, { ComponentProps } from 'react'

import { OrderStatusTypeString } from '@/entities/gridTables/model/OrderStatusType'
import { BusinessModelEnum } from '@/entities/user/models/BusinessModel'
import { useAppSelector } from '@/shared/store'
import { pxToRem } from '@/shared/styles/themes'
import { faChevronRight } from '@fortawesome/pro-regular-svg-icons'
import { faCarSide, faDownload, faFileLines, faMailboxFlagUp, faTruck, faUpload } from '@fortawesome/pro-solid-svg-icons'
import { Box, Chip, styled, Typography } from '@mui/material'

import { Icon } from '../Icon/Icon'
import { SearchResult } from './models/SearchResult'

export type SearchResultOptionProps = ComponentProps<typeof StyledOption> & {
  option: SearchResult
}

export const SearchResultOption: React.FC<SearchResultOptionProps> = ({ option, ...props }) => {
  const isCourierModel = useAppSelector(state => state.user.businessModel === BusinessModelEnum.Courier)

  if (option.orderId === 'no-results') {
    return (
      <Box sx={{ padding: pxToRem(8) }}>
        <Typography variant="body1">
          No result found
        </Typography>
      </Box>
    )
  }

  return (
    <StyledOption {...props}>
      <StyledHeader>
        <StyledColumn>
          <StyledOrderNumber>
            {option.orderNumber}
            {option?.statusType && (
              <StyledChip variant="outlined" label={OrderStatusTypeString[option.statusType]} />
            )}
            {option.isPickupOrder ? (
              <StyledIcon icon={faUpload} sx={{ color: '#0c64c4' }} />
            ) : (
              <StyledIcon icon={faDownload} />
            )}
          </StyledOrderNumber>
          <StyledSecondaryTypography>{option.customerName} {option.customerNumber ? `(${option.customerNumber})` : null}</StyledSecondaryTypography>
        </StyledColumn>
        {option.runName ? (
          <Box sx={{
            display: 'inline-flex',
            alignItems: 'center',
            flexWrap: 'nowrap',
          }}>
            <StyledTypography variant="body1">
              {option.scheduleName}
            </StyledTypography>
            <StyledIcon icon={faChevronRight} sx={{ margin: '0 4px' }} />
            <StyledRunInfo>
              <StyledIcon icon={isCourierModel ? faCarSide : faTruck} sx={{ color: option.uiColor }} />
              <StyledTypography variant="body1">
                {option.runName}
              </StyledTypography>
            </StyledRunInfo>
          </Box>
        ) : option.scheduleName ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
            <StyledTypography variant="body1">
              {option.scheduleName}
            </StyledTypography>
            <StyledIcon icon={faChevronRight} sx={{ margin: '0 4px' }} />
            <StyledRunInfo>
              <StyledIcon icon={faFileLines} sx={{ color: option.uiColor }} />
              <StyledTypography variant="body1">
                Unassigned
              </StyledTypography>
            </StyledRunInfo>
          </Box>
        ) : (
          <StyledRunInfo>
            <StyledIcon icon={faMailboxFlagUp} sx={{ color: option.uiColor }} />
            <Typography variant="body1">
              Inbox
            </Typography>
          </StyledRunInfo>
        )}
      </StyledHeader>
    </StyledOption>
  )
}

const StyledOption = styled('div')`
  padding: ${pxToRem(8)};
  cursor: pointer;

  &:hover {
    background-color: ${({ theme }) => theme.palette.action.hover};
  }
`

const StyledHeader = styled('div')`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const StyledColumn = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 2px;
`

const StyledOrderNumber = styled('div')`
  display: flex;
  align-items: center;
  gap: ${pxToRem(4)};
  font-weight: 500;
  color: ${({ theme }) => theme.palette.text.primary};
`

const StyledIcon = styled(Icon)`
  font-size: ${pxToRem(14)};
  flex-shrink: 0;
`

const StyledSecondaryTypography = styled(Typography)`
  font-size: ${pxToRem(14)};
  color: ${({ theme }) => theme.palette.text.secondary};
`

const StyledRunInfo = styled('div')`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: ${pxToRem(4)};
`

const StyledTypography = styled(Typography)`
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis
`

const StyledChip = styled(Chip)`
  height: ${pxToRem(24)}
`